import { Button, DatePicker, Icon, Input, List, Popconfirm, Popover } from "antd";
import { RangePickerValue } from "antd/lib/date-picker/interface";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSingleReportLink } from "../../../../Logic/ReportLogic";
import { IUser } from "../../../../Models/IUser";

import {
    getNotificationsInitiate,
    setNotificationsByLinkIdViewedInitiate,
    setNotificationsDrawerVisible,
    setNotificationsFilterDates,
    updateAllNotificationsActiveStatusInitiate,
    updateAllNotificationsReadStatusInitiate,
    updateNotificationInitiate
} from "../../Actions/NotificationActions";
import { App } from "../../Enums/App";
import { NotificationType } from "../../Enums/NotificationType";
import { INotification } from "../../Models/Notification";
import { UpdateNotificationRequest } from "../../Models/Requests/Notifications/UpdateNotificationRequest";
import {
    loadingNotificationsSelector,
    notificationsDrawerVisibleSelector,
    notificationsSelector,
    notificationsFilterDatesSelector
} from "../../Selectors/NotificationsSelectors";
import { SideNav } from "../SideNav";
import {
    getBaseUrlFromNotificationType,
    getFilteredNotifications,
    getFixedNotificationDates,
    getNotificationCursor,
    getNotificationDateTimeValue,
    getNotificationIcon
} from "./NotificationsLogic";
import { Circle, DateTimeText, DeleteConfirmWrapper, NotificationIconWrapper, NotificationsEmptyWrapper, StyledList, StyledModalHeader, StyledUl, TitleText } from "./NotificationsStyled";

/** how the date displays in the date picker */
const dateFormat = 'MM/DD/YYYY';

interface INotificationsModalProps {
    user: IUser;
};

export const NotificationsModal = (props: INotificationsModalProps): JSX.Element => {
    /** misc */
    const dispatch = useDispatch();
    /** state */
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filteredNotifications, setFilteredNotifications] = useState<INotification[]>([]);
    const [confirmingNotification, setConfirmingNotification] = useState<INotification>(null);
    /** selectors */
    const notifications: INotification[] = useSelector(notificationsSelector);
    const loading: boolean = useSelector(loadingNotificationsSelector);
    const visible: boolean = useSelector(notificationsDrawerVisibleSelector);
    const filterDates: [Date, Date] = useSelector(notificationsFilterDatesSelector);
    /** effects */
    useEffect(() => {
        if (!visible) {
            setSearchTerm("");
            setFilteredNotifications([]);
            setConfirmingNotification(null);
        }
    }, [visible]);

    useEffect(() => {
        const filtered: INotification[] = getFilteredNotifications(notifications, searchTerm, filterDates);
        setFilteredNotifications(filtered);
    }, [notifications, searchTerm, filterDates]);

    const onViewAll = (): void => {
        //TODO: there is no design for this yet.
        console.log("onViewAll - no design for this yet");
    };

    const onNotificationClick = (n: INotification): void => {
        /** don't allow clicks for reports that are not complete */
        if (n.notificationType === NotificationType.MatchReportComplete
            || n.notificationType === NotificationType.MobilyticsReportComplete) {
            /** mark associated notifications as viewed */
            dispatch(setNotificationsByLinkIdViewedInitiate(n.linkId));
            /** navigate to the report */
            dispatch(setNotificationsDrawerVisible(false));
            const reportType = NotificationType[n.notificationType].toLowerCase().includes("matchreport")
                ? App.Match
                : App.Mobilytics;

            window.location.href = getSingleReportLink(props.user, reportType, n.linkId);
        }
    };

    const confirmDelete = (n: INotification): void => {
        setConfirmingNotification(n);
    };

    const cancelConfirmDelete = (): void => {
        setConfirmingNotification(null);
    };

    /** simply change the read status for the clicked on notification */
    const onReadStatusClick = (item: INotification): void => {
        const foundNotification: INotification = notifications.find((n: INotification) => {
            return n.id === item.id;
        });
        if (foundNotification) {
            foundNotification.isRead = !foundNotification.isRead;
            const request = new UpdateNotificationRequest(
                foundNotification
            );
            dispatch(updateNotificationInitiate(request));
        }
    };
    /** simply change the active status for the clicked on notification */
    const onDeleteClick = (item: INotification): void => {
        const foundNotification: INotification = notifications.find((n: INotification) => {
            return n.id === item.id;
        });
        if (foundNotification) {
            foundNotification.isActive = false;
            const request = new UpdateNotificationRequest(foundNotification);
            dispatch(updateNotificationInitiate(request));
        }
    };
    /** change read status for all notifications */
    const onReadStatusAllClick = (allRead: boolean): void => {
        dispatch(updateAllNotificationsReadStatusInitiate(allRead));
    };
    /** change active status for all notifications */
    const onActiveStatusAllClick = (allActive: boolean): void => {
        dispatch(updateAllNotificationsActiveStatusInitiate(allActive));
    };
    /** sets the search term based on user input */
    const onSearchChange = (value: string): void => {
        setSearchTerm(value);
    };
    /** simply sets the filter dates for start and end date based on the user's choices */
    const onDateRangeChange = (dates: RangePickerValue, dateStrings: [string, string]): void => {
        /** GB:
         * There has to be a bug with this AntD <RangePicker> component. If you choose a date range
         * that is a day apart (like a Thursday and Friday), the second value will be undefined, and the
         * first value (which should be the start date) will actually be the end date.
         *
         * So this code accounts for that scenario. PSH! Interestingly enough, this does NOT happen if you choose
         * the same date for the start and end date.
         */
        let startDate: Date = dates[0].toDate();
        let endDate: Date = dates[1]?.toDate();

        if (endDate === undefined) {
            const start: Date = dates[0].toDate();
            start.setDate(start.getDate() - 1);
            startDate = start;
            endDate = dates[0].toDate();
        }
        const dateRange: [Date, Date] = getFixedNotificationDates(startDate, endDate);
        dispatch(setNotificationsFilterDates(dateRange));
        dispatch(getNotificationsInitiate(dateRange));
    };

    const onDrawerClose = (): void => {
        dispatch(setNotificationsDrawerVisible(false));
    };

    const getEmptyText = (): JSX.Element => {
        let text = "";
        if (loading) {
            text = "Loading...";
        } else if (notifications.length > 0 && searchTerm.trim().length > 0 && filteredNotifications.length === 0) {
            /** they have notifications, but they've filtered them out via search term */
            text = "No notifications containing this term.";
        } else {
            text = "No notifications within this date range.";
        }
        return (
            <NotificationsEmptyWrapper>{text}</NotificationsEmptyWrapper>
        );
    };

    const allNotificationsAreRead: boolean = notifications.every((n: INotification) => {
        return n.isRead;
    });
    const allNotificationsAreUnread: boolean = notifications.every((n: INotification) => {
        return !n.isRead;
    });
    const allowDeleteAll: boolean = notifications.some((n: INotification) => {
        return n.isActive;
    });

    return (
        <SideNav
            onCloseCallback={onDrawerClose}
            placement="right"
            visible={visible}
            width={"631px"}
            bodyStyle={{
                padding: 0,
            }}
            mask={true}
            maskClosable={true}
            closeable={true}>
            <StyledModalHeader>
                <div style={{
                    fontSize: "16px"
                }}>NOTIFICATIONS</div>
                {notifications.length > 0 && (
                    <Popover
                        placement={"bottomRight"}
                        trigger="click"
                        content={(
                            <StyledUl>
                                <Popconfirm
                                    title="Are you sure to mark all your notifications as read?"
                                    onConfirm={() => !allNotificationsAreRead && onReadStatusAllClick(true)}
                                    okText="Yes, mark all as read"
                                    cancelText="No"
                                    placement="left"
                                    disabled={allNotificationsAreRead}
                                >
                                    <li className={allNotificationsAreRead ? "disabled" : ""}>Mark All As Read</li>
                                </Popconfirm>
                                <Popconfirm
                                    title="Are you sure to mark all your notifications as unread?"
                                    onConfirm={() => !allNotificationsAreUnread && onReadStatusAllClick(false)}
                                    okText="Yes, mark all as unread"
                                    cancelText="No"
                                    placement="left"
                                    disabled={allNotificationsAreUnread}
                                >
                                    <li className={allNotificationsAreUnread ? "disabled" : ""}>Mark All As Unread</li>
                                </Popconfirm>
                                <Popconfirm
                                    title="Are you sure to delete all your notifications?"
                                    onConfirm={() => allowDeleteAll && onActiveStatusAllClick(false)}
                                    okText="Yes, delete all my notifications"
                                    cancelText="No"
                                    placement="left"
                                    disabled={!allowDeleteAll}
                                >
                                    <li className={!allowDeleteAll ? "disabled" : ""}>Delete All</li>
                                </Popconfirm>
                            </StyledUl>
                        )}>
                        <Icon
                            type={"setting"}
                            style={{
                                cursor: "pointer",
                                fontSize: "20px",
                                marginLeft: "auto"
                            }}
                        />
                    </Popover>
                )}
            </StyledModalHeader>
            <>
                <div style={{
                    display: "flex",
                    padding: "1rem"
                }}>
                    <DatePicker.RangePicker
                        disabled={loading}
                        allowClear={false}
                        defaultValue={[
                            moment(filterDates[0]),
                            moment(filterDates[1]),
                        ]}
                        value={[
                            moment(filterDates[0]),
                            moment(filterDates[1]),
                        ]}
                        onChange={(dates: RangePickerValue, dateStrings: [string, string]) => onDateRangeChange(dates, dateStrings)}
                        disabledDate={(d: moment.Moment) => d.startOf("day") > moment(new Date()).startOf("day")}
                        style={{
                            marginRight: "9px"
                        }}
                        format={dateFormat}
                    />
                    <Input.Search
                        placeholder="Search Notifications..."
                        allowClear={true}
                        disabled={notifications.length === 0 || loading}
                        loading={loading}
                        onChange={(e) => onSearchChange(e.target.value)} />
                </div>
                <StyledList
                    bordered={true}
                    loading={loading}
                    dataSource={filteredNotifications}
                    locale={{
                        emptyText: getEmptyText()
                    }}
                    renderItem={(item: INotification) => (
                        <List.Item
                            style={{
                                position: "relative",
                                height: "80px"
                            }}>
                            <NotificationIconWrapper
                                isRead={item.isRead}
                                type={item.notificationType}>
                                {getNotificationIcon(item)}
                            </NotificationIconWrapper>
                            <div
                                style={{
                                    cursor: getNotificationCursor(item.notificationType),
                                    marginRight: "50px",
                                    width: "100%"
                                }}
                                onClick={() => onNotificationClick(item)}>
                                <TitleText
                                    type={item.notificationType}
                                    isRead={item.isRead}>
                                    {item.title}
                                </TitleText>
                                <div>{item.description}</div>
                            </div>
                            <DateTimeText
                                isRead={item.isRead}>
                                {getNotificationDateTimeValue(item.createdDate)}
                            </DateTimeText>
                            <Circle
                                type={item.notificationType}
                                read={item.isRead} />
                            <Popover
                                placement={"bottomRight"}
                                trigger={"hover"}
                                content={(
                                    <StyledUl>
                                        <li
                                            onClick={() => !item.isRead && onReadStatusClick(item)}
                                            style={{
                                                opacity: item.isRead
                                                    ? "50%"
                                                    : "initial"
                                            }}>
                                            Mark as Read
                                        </li>
                                        <li
                                            onClick={() => item.isRead && onReadStatusClick(item)}
                                            style={{
                                                opacity: !item.isRead
                                                    ? "50%"
                                                    : "initial"
                                            }}>
                                            Mark as Unread
                                        </li>
                                        <li onClick={() => confirmDelete(item)}>Delete</li>
                                    </StyledUl>
                                )}>
                                <Icon
                                    type="more"
                                    style={{
                                        cursor: "pointer",
                                        fontSize: "20px"
                                    }} />
                            </Popover>
                            {confirmingNotification?.id === item.id && (
                                <DeleteConfirmWrapper>
                                    <div style={{
                                        fontWeight: "bold",
                                        marginBottom: "6px"
                                    }}>Delete Notification?</div>
                                    <div style={{
                                        display: "flex",
                                    }}>
                                        <Button
                                            onClick={cancelConfirmDelete}
                                            style={{ marginRight: "6px" }}>No</Button>
                                        <Button
                                            onClick={() => onDeleteClick(item)}
                                            style={{ backgroundColor: "#2e3d49", color: "#fff" }}>Yes</Button>
                                    </div>
                                </DeleteConfirmWrapper>
                            )}
                        </List.Item>
                    )}
                />
            </>
            {/* <StyledModalFooter>
                {notifications.length > 0 && !loading && (
                    <span
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                        onClick={onViewAll}>View All</span>
                )}
            </StyledModalFooter> */}
        </SideNav>
    );
};
